<template>
  <div class="modal fade" id="modal" ref="modal" data-bs-backdrop="static" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content border-0 shadow-lg" style="border-radius: 16px;">

        <div class="bg-warning modal-header pe-4 text-dark" style="border-radius: 16px 16px 0 0;">
          <div>
            <h4 class="modal-title fw-bold mb-0">
              <i class="fa-regular fa-calendar-check me-2" />
              {{ $t('Table') + ' ' + name }}
            </h4>
            <div class="small opacity-75 mt-1">
              <i class="fa-regular fa-clock me-1" />
              {{ reserveAt ? formatTime(reserveAt) : '-' }} &nbsp;|&nbsp;
              <i class="fa-solid fa-users me-1" />
              {{ reservePax + ' ' + $t('_Table.Guests') }}
            </div>
          </div>
          <button type="button" class="btn-close" data-bs-dismiss="modal" />
        </div>

        <div class="modal-body px-4 py-4">

          <div class="card border-0 shadow-sm rounded-3 mb-2">
            <div class="card-body">
              <div class="row g-3">
                <div class="col-12">
                  <div class="text-muted small fw-bold mb-1">
                    <i class="fa-solid fa-user me-1" />
                    {{ $t('Customer Name') }}
                  </div>
                  <div class="fw-bold text-dark">{{ reserveName || '-' }}</div>
                </div>

                <div class="col-12" v-if="reserveTel">
                  <div class="text-muted small fw-bold mb-1">
                    <i class="fa-solid fa-phone me-1" />
                    {{ $t('Phone Number') }}
                  </div>
                  <div class="fw-bold text-dark">{{ reserveTel }}</div>
                </div>

                <div class="col-6">
                  <div class="text-muted small fw-bold mb-1">
                    <i class="fa-regular fa-calendar-check me-1" />
                    {{ $t('Date and Time') }}
                  </div>
                  <div class="fw-bold text-dark">{{ reserveAt ? formatDateTime(reserveAt) : '-' }}</div>
                </div>

                <div class="col-6">
                  <div class="text-muted small fw-bold mb-1">
                    <i class="fa-solid fa-users me-1" />
                    {{ $t('Number of guests') + ' (Pax)' }}
                  </div>
                  <div class="fw-bold text-dark">{{ reservePax }}</div>
                </div>

                <div class="col-12" v-if="reserveNote">
                  <div class="text-muted small fw-bold mb-1">
                    <i class="fa-solid fa-sticky-note me-1" />
                    {{ $t('Note') }}
                  </div>
                  <div class="text-dark">{{ reserveNote }}</div>
                </div>
              </div>
            </div>
          </div>

          <div class="row g-2">
            <div class="col-12">
              <ladda
                :text="$t('Open Table')"
                :btn-class="'btn btn-theme w-100 py-3 fw-bold fs-5 text-white'"
                style="border-radius: 12px; box-shadow: 0 4px 10px rgba(234, 100, 6, 0.3);"
                :loading="loadingOpen"
                icon="<i class='fa-solid fa-check-circle'></i>"
                @click="handleOpenTable"
              />
            </div>
            <!-- QR สั่งอาหาร ซ่อนเมื่อแพ็กไม่มี feature_qr_ordering — ย้ายโต๊ะขยายเต็มแถวแทน -->
            <div v-if="qrOrdering" class="col-6">
              <ladda
                :text="$t('QR Order')"
                :btn-class="'btn btn-outline-primary w-100 py-3 fw-bold rounded-3'"
                icon="<i class='fa-solid fa-qrcode'></i>"
                @click="showQrCode = !showQrCode"
              />
            </div>
            <div :class="qrOrdering ? 'col-6' : 'col-12'">
              <ladda
                :text="$t('Move Table')"
                :btn-class="'btn btn-outline-secondary w-100 py-3 fw-bold rounded-3'"
                icon="<i class='fa-solid fa-arrow-right-arrow-left'></i>"
                data-bs-dismiss="modal"
                @click="$emit('moveTable')"
              />
            </div>

            <div v-if="qrOrdering" class="col-12" v-show="showQrCode">
              <div class="card border-0 shadow-sm rounded-3">
                <div class="card-body text-center py-4">
                  <div class="fw-bold mb-2">{{ $t('Scan to Order') }}</div>
                  <canvas ref="qrCanvas"></canvas>
                  <div class="mt-3 d-flex justify-content-center gap-2 flex-wrap">
                    <button
                      type="button"
                      class="btn btn-sm btn-dark px-4 rounded-3"
                      @click="printQrCode"
                    >
                      <i class="fa-solid fa-print me-2" />
                      {{ $t('Print QR Code') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-12">
              <ladda
                :text="$t('Cancel Reservation')"
                :btn-class="'btn btn-outline-danger w-100 py-3 fw-bold rounded-3'"
                :loading="loadingCancel"
                icon="<i class='fa-solid fa-ban'></i>"
                @click="handleCancelReserve"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="js">
import { POSReservations } from '@/services/endpoint/pos.service';
import Ladda from '@/plugins/Ladda.vue';
import moment from 'moment';
import QRCode from 'qrcode';
import { printQrSlips } from '@/utils/qrPrint';

export default {
  props: {
    id: {
      type: Number,
      default: 0
    },
    reservationId: {
      type: Number,
      default: 0
    },
    name: {
      type: String,
      default: ''
    },
    reserveName: {
      type: String,
      default: ''
    },
    reserveTel: {
      type: String,
      default: ''
    },
    reserveAt: {
      type: String,
      default: ''
    },
    reservePax: {
      type: [Number, String],
      default: 0
    },
    reserveNote: {
      type: String,
      default: ''
    },
    serviceToken: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loadingOpen: false,
      loadingCancel: false,
      showQrCode: false,
    }
  },
  components: {
    Ladda
  },
  methods: {
    formatDateTime(value) {
      return moment(value).format('YYYY-MM-DD HH:mm');
    },
    formatTime(value) {
      return moment(value).format('HH:mm');
    },
    async handleOpenTable() {
      this.loadingOpen = true;
      try {
        await POSReservations.open(this.reservationId);
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Open Table Success!'));
        $(this.$refs.modal).modal('hide');
      } catch (err) {
        if (err?.response?.data?.message) {
          this.$toast.warn(err.response.data.message);
        }
      } finally {
        this.loadingOpen = false;
      }
    },
    async renderQrCode() {
      await this.$nextTick();
      if (this.$refs.qrCanvas) {
        QRCode.toCanvas(this.$refs.qrCanvas, this.orderUrl, { width: 200, margin: 1 });
      }
    },
    async printQrCode() {
      try {
        await printQrSlips(
          [{ heading: this.$t('Table') + ' ' + this.name, subtitle: this.$t('Scan to Order'), url: this.orderUrl }]
        );
      } catch (e) {
        console.error('QR print error:', e);
        this.$toast.error(this.$t('Print failed') + ': ' + (e.message || e));
      }
    },
    async handleCancelReserve() {
      this.loadingCancel = true;
      try {
        await POSReservations.cancel(this.reservationId);
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Cancel Reservation Success!'));
        $(this.$refs.modal).modal('hide');
      } catch (err) {
        if (err?.response?.data?.message) {
          this.$toast.warn(err.response.data.message);
        }
      } finally {
        this.loadingCancel = false;
      }
    },
  },
  watch: {
    showQrCode(val) {
      if (val) this.renderQrCode();
    }
  },
  computed: {
    // แพ็กมีสั่งผ่าน QR ไหม — ไม่มี = ซ่อนปุ่ม QR (ลูกค้าสแกนไปก็เจอจอ "ระบบสั่งอาหารไม่พร้อมใช้งาน")
    qrOrdering() {
      return this.$hasFeature('feature_qr_ordering');
    },
    orderUrl() {
      return process.env.VUE_APP_URL + '/service/' + this.serviceToken;
    }
  },
  mounted() {
    $(this.$refs.modal).modal('show');

    $(this.$refs.modal).on('hidden.bs.modal', () => {
      this.$emit('close');
    });
  },
}
</script>
